/**
 * Random colour themes (the Themes panel's Random button). Gordon 2026-11-02:
 * "way more random colors and more high contrast text and icons — all the
 * seasons' colours, nature, pastel; now it's fluorescent only".
 *
 * Each roll picks a colour family (seasons, nature, pastel, fluorescent,
 * jewel, vintage, sunset, ink), then a background and an accent inside it.
 * Text, icons and outlines are always near-black or near-white against the
 * background (WCAG AA or better); button text is black or white, whichever
 * reads better on the accent. Pure: pass `rand` to make a roll repeatable.
 */

type Range = [number, number];
interface Family {
  name: string;
  /** Background hues to pick from (degrees); each entry is a range. */
  hues: Range[];
  /** Dark background chance, 0–1. */
  dark: number;
  bgS: Range;
  darkL: Range;
  lightL: Range;
  /** Accent: hue offsets from the background hue, or absolute hues. */
  accent: { offsets?: Range[]; hues?: Range[]; s: Range; l: Range };
}

export const PALETTE_FAMILIES: Family[] = [
  { name: 'spring', hues: [[85, 150], [320, 350]], dark: 0.25, bgS: [35, 60], darkL: [10, 15], lightL: [92, 96], accent: { hues: [[90, 140], [325, 350], [48, 58]], s: [60, 80], l: [42, 52] } },
  { name: 'summer', hues: [[185, 210], [38, 55]], dark: 0.3, bgS: [45, 75], darkL: [9, 14], lightL: [92, 96], accent: { hues: [[0, 15], [170, 190], [45, 55]], s: [75, 95], l: [48, 56] } },
  { name: 'autumn', hues: [[12, 42]], dark: 0.7, bgS: [40, 65], darkL: [9, 15], lightL: [89, 93], accent: { hues: [[18, 40], [45, 52], [0, 10]], s: [70, 88], l: [48, 56] } },
  { name: 'winter', hues: [[195, 235]], dark: 0.6, bgS: [15, 40], darkL: [7, 13], lightL: [94, 97], accent: { hues: [[180, 200], [330, 350], [255, 275]], s: [55, 80], l: [55, 65] } },
  { name: 'forest', hues: [[95, 155]], dark: 0.75, bgS: [30, 55], darkL: [7, 13], lightL: [90, 94], accent: { hues: [[38, 55], [75, 100]], s: [55, 75], l: [48, 58] } },
  { name: 'ocean', hues: [[185, 225]], dark: 0.65, bgS: [45, 75], darkL: [8, 14], lightL: [92, 96], accent: { hues: [[160, 185], [10, 25]], s: [65, 85], l: [50, 58] } },
  { name: 'desert', hues: [[22, 42]], dark: 0.25, bgS: [30, 55], darkL: [10, 15], lightL: [86, 92], accent: { hues: [[8, 22], [190, 205]], s: [55, 75], l: [40, 50] } },
  { name: 'pastel', hues: [[0, 360]], dark: 0, bgS: [55, 80], darkL: [10, 14], lightL: [93, 97], accent: { offsets: [[120, 240]], s: [55, 75], l: [70, 80] } },
  { name: 'fluorescent', hues: [[0, 360]], dark: 0.95, bgS: [15, 45], darkL: [4, 8], lightL: [96, 98], accent: { hues: [[318, 330], [100, 125], [175, 190], [55, 64], [20, 30]], s: [100, 100], l: [52, 60] } },
  { name: 'jewel', hues: [[145, 160], [215, 230], [270, 290], [340, 355]], dark: 0.9, bgS: [45, 70], darkL: [9, 15], lightL: [90, 94], accent: { hues: [[42, 50]], s: [75, 90], l: [52, 60] } },
  { name: 'vintage', hues: [[28, 50]], dark: 0.2, bgS: [22, 40], darkL: [11, 16], lightL: [86, 91], accent: { hues: [[175, 190], [42, 50], [6, 14]], s: [45, 62], l: [38, 48] } },
  { name: 'sunset', hues: [[255, 295]], dark: 0.85, bgS: [35, 60], darkL: [8, 14], lightL: [91, 95], accent: { hues: [[15, 32], [330, 345]], s: [80, 95], l: [55, 62] } },
  { name: 'ink', hues: [[0, 360]], dark: 0.5, bgS: [4, 12], darkL: [6, 11], lightL: [95, 98], accent: { hues: [[0, 360]], s: [70, 90], l: [45, 55] } },
];

// Whole numbers, so the contrast checked is the contrast of the CSS value written.
const pick = (r: Range, rand: () => number) => Math.round(r[0] + (r[1] - r[0]) * rand());
const pickRange = (list: Range[], rand: () => number) => list[Math.floor(rand() * list.length) % list.length];
const hsl = (h: number, s: number, l: number) => `${Math.round(((h % 360) + 360) % 360)} ${Math.round(s)}% ${Math.round(l)}%`;

/** Relative luminance of an HSL colour (h deg, s/l 0–100). */
export function luminanceHsl(h: number, s: number, l: number): number {
  const S = s / 100, L = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = S * Math.min(L, 1 - L);
  const f = (n: number) => L - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(f(0)) + 0.7152 * lin(f(8)) + 0.0722 * lin(f(4));
}
export function contrast(a: number, b: number): number {
  const [hi, lo] = a > b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}

export interface RandomPalette { family: string; dark: boolean; colors: Record<string, string> }

export function randomPalette(rand: () => number = Math.random, avoid?: string): RandomPalette {
  let fam = PALETTE_FAMILIES[Math.floor(rand() * PALETTE_FAMILIES.length) % PALETTE_FAMILIES.length];
  if (avoid && fam.name === avoid) fam = PALETTE_FAMILIES[(PALETTE_FAMILIES.indexOf(fam) + 1) % PALETTE_FAMILIES.length];
  const dark = rand() < fam.dark;
  const hue = pick(pickRange(fam.hues, rand), rand);
  const bgS = pick(fam.bgS, rand);
  const bgL = dark ? pick(fam.darkL, rand) : pick(fam.lightL, rand);

  // Accent: from the family, nudged until it stands out from the background.
  const aHue = (fam.accent.hues ? pick(pickRange(fam.accent.hues, rand), rand) : hue + pick(pickRange(fam.accent.offsets || [[150, 210]], rand), rand)) % 360;
  const aS = pick(fam.accent.s, rand);
  let aL = pick(fam.accent.l, rand);
  const bgLum = luminanceHsl(hue, bgS, bgL);
  for (let i = 0; i < 12 && contrast(luminanceHsl(aHue, aS, aL), bgLum) < 3; i++) aL += dark ? 4 : -4;
  aL = Math.max(20, Math.min(85, aL));
  // Button text black or white — and the accent moved lighter/darker until
  // that text reads (mid-tone oranges and greens fail with both otherwise).
  const INK = luminanceHsl(0, 0, 4); // the "black" button text, 0 0% 4%
  const readable = (h: number, l: number) => {
    // Pick the text that reads better now, then move the accent away from it
    // (one direction only — flipping sides can oscillate around the middle).
    const lu0 = luminanceHsl(h, aS, l);
    const onBlack = contrast(lu0, INK) >= contrast(lu0, 1);
    let L = l;
    for (let i = 0; i < 45 && contrast(luminanceHsl(h, aS, L), onBlack ? INK : 1) < 4.6; i++) {
      L = Math.max(3, Math.min(97, L + (onBlack ? 2 : -2)));
    }
    return { l: L, on: onBlack ? '0 0% 4%' : '0 0% 100%' };
  };
  const a1 = readable(aHue, aL);
  aL = a1.l;
  const onAccent = a1.on;
  // A second accent a little apart, same rules.
  const a2Hue = Math.round(aHue + 40 + rand() * 40) % 360;
  const a2 = readable(a2Hue, aL);
  const onA2 = a2.on;

  // Text, icons and lines: near-white on dark, near-black on light — always high contrast.
  const fg = dark ? hsl(hue, 12, 97) : hsl(hue, 30, 7);
  const mutedFg = dark ? hsl(hue, 12, 80) : hsl(hue, 18, 26);
  const shift = (d: number) => hsl(hue, bgS, Math.max(2, Math.min(98, bgL + (dark ? d : -d))));
  const primary = hsl(aHue, aS, aL);

  return {
    family: fam.name,
    dark,
    colors: {
      background: hsl(hue, bgS, bgL),
      foreground: fg,
      card: shift(4),
      cardForeground: fg,
      popover: shift(5),
      popoverForeground: fg,
      border: dark ? hsl(hue, Math.min(bgS, 30), bgL + 22) : hsl(hue, Math.min(bgS, 30), bgL - 26),
      muted: shift(8),
      mutedForeground: mutedFg,
      primary,
      primaryForeground: onAccent,
      accent: hsl(a2Hue, aS, a2.l),
      accentForeground: onA2,
      secondary: shift(8),
      secondaryForeground: fg,
      sidebarBackground: shift(-2),
      sidebarForeground: fg,
      sidebarPrimary: primary,
      sidebarPrimaryForeground: onAccent,
      sidebarAccent: shift(7),
      sidebarAccentForeground: fg,
      headerBackground: shift(2),
      playerBackground: shift(-2),
      playerToggleBackground: primary,
      // Outlines and menu icons at full strength (globals.css data-bb-lines / data-bb-icons).
      lines: fg,
      icons: fg,
    },
  };
}
